import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import VideoGuideSection from './VideoGuideSection';
import { Box } from '@mui/material';

const UserGuide = () => {
  const { isAuthenticated, user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [openFaqItem, setOpenFaqItem] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(null);
  const resizeTimeoutRef = useRef(null);

  const styles = {
    paragraph: {
      textAlign: 'left'
    }
  };
  
  
  // Video guide data (translated to English)
  const videoGuideData = [
    {
      id: 1,
      title: "Quick Start Guide for Costing Wizard",
      duration: "05:11",
      videoId: "XG-TpKj0D1w"
    },
    {
      id: 2,
      title: "Example Project Explanation in Costing Wizard",
      duration: "03:25",
      videoId: "nn72EUT2hek"
    },
    {
      id: 3,
      title: "How to edit / extend project with AI",
      duration: "5:45",
      videoId: "DtMMp_dFQGc"
    },
  ];

  // Initialize with first video
  useEffect(() => {
    if (videoGuideData.length > 0 && !currentVideo) {
      setCurrentVideo(videoGuideData[0]);
    }
  }, [videoGuideData, currentVideo]);

  const handleVideoClick = (video) => {
    setCurrentVideo(video);
  };

  const handleShowAllVideos = () => {
    window.open('https://www.youtube.com/watch?v=DtMMp_dFQGc&list=PLRpCe7B0Fq5qTXH5zJJ5Q30SPJPIDRPr5', '_blank', 'noopener,noreferrer');
  };

  // Debounced resize handler
  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    resizeTimeoutRef.current = setTimeout(() => {
      // Force a re-render of components that might need resizing
      setIsNavOpen(prev => prev);
    }, 300);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [handleResize]);

  const sectionsData = useMemo(() => [
    {
      id: 'h2-overview',
      title: '🏗️ Overview',
      keywords: ['overview', 'general', 'features', 'key features', 'costing', 'wizard', 'advanced', 'professionals', 'contractors', 'entrepreneurs', 'quotes', 'cost calculation', 'project management', 'artificial intelligence'],
      searchableContent: "Costing Wizard is an advanced tool designed for professionals, contractors, and entrepreneurs to create accurate and professional price quotes. The system combines smart cost calculations, project management, and artificial intelligence for a perfect user experience. Key features: automatic cost calculation, custom templates, artificial intelligence, professional export, currency management, cloud backup, multi-device access. Perfect for: contractors, professionals, planners, consultants, and anyone needing a professional tool for cost calculation and price quotes. All users get free access to all features with basic limitations. You can upgrade to a Premium or Enterprise subscription at any time."
    },
    {
      id: 'h2-getting-started-main',
      title: '🚀 Getting Started',
      keywords: ['getting started', 'start', 'registration', 'login', 'download', 'application', 'app store', 'google play', 'email', 'first project', 'create project', 'project list screen', 'add new project', 'add project with AI'],
      searchableContent: "First steps. Registration and login process: Download the app from Apple or Google. Go to costingwizard.com or click Register in the app. Click on Register and create a new account. Verify your email address. Log in through the app. Create your first project! All users get free access to all features with basic limitations. You can upgrade to a Premium or Enterprise subscription at any time. Creating your first project. Project list screen: Welcome to the CostingWizard app! On this screen, you can see all the projects you have already created. Each row in the table represents a project and includes: date, ID, description. Creating a new project: At the bottom of the screen, you will find two buttons: 1. Add new project - to create a project manually. 2. Add project with AI - to create a project automatically using artificial intelligence; you will be asked to type a general description of the project, and the app will build a complete project."
    },
    {
      id: 'h2-add-chapters-items',
      title: '🧱 Adding Chapters and Items to a Project',
      keywords: ['chapters', 'items', 'budget', 'tasks', 'breakdown', 'construction', 'electricity', 'plumbing', 'quantity', 'unit of measure', 'price per unit', 'notes', 'fixed price', 'resource-based pricing', 'plaster', 'paint', 'daily worker'],
      searchableContent: "After creating a new project, you will build its budget by adding chapters (main areas/stages) and for each chapter – items (detailed tasks). What is a chapter? A chapter is a group of works from the same field. What is an item? An item is a specific task or component within a chapter. For each item, you define: description, quantity, unit of measure, price per unit, notes. How to add chapters and items in practice? Adding a chapter, adding an item to a chapter. Defining a price for an item: fixed price or resource-based pricing."
    },
    {
      id: 'h2-add-location-quantities',
      title: '📍 Adding Location and Quantities to an Item',
      keywords: ['location', 'quantities', 'multipliers', 'width', 'length', 'floor', 'quantity calculation', 'total cost'],
      searchableContent: "At this stage, you can enter the item's location in the project and the actual quantity, determined by four multipliers. How do the multipliers work? Quantity = Multiplier 1 × Multiplier 2 × Multiplier 3 × Multiplier 4. Example: Removing flooring in a bathroom, multipliers for the width and length of the room."
    },
    {
      id: 'h2-dashboard',
      title: '📊 Dashboard',
      keywords: ['dashboard', 'projects', 'statistics', 'account management', 'subscription details', 'currency', 'usage data', 'PDF export', 'Excel export', 'AI calls', 'subscription limits'],
      searchableContent: "The dashboard is your command center. Subscription details: personal information, subscription status, currency settings. Usage data: PDF and Excel export counter, artificial intelligence usage, number of saved projects."
    },
    {
      id: 'h2-ai-features',
      title: '🤖 Artificial Intelligence',
      keywords: ['artificial intelligence', 'AI', 'create project with AI', 'project description', 'kitchen renovation', 'initial estimate', 'edit'],
      searchableContent: "Creating projects using artificial intelligence. Describe what you want to build, and the system will create a list of items. How to use AI: Describe in detail, include dimensions, specify type of work, check the result, edit as needed. The AI provides an initial estimate only."
    },
    {
      id: 'h2-advanced-features',
      title: '✨ Advanced Features',
      keywords: ['advanced features', 'duplicate projects', 'custom templates', 'automatic calculation', 'notes', 'discounts'],
      searchableContent: 'Duplicate projects. Custom templates. Automatic calculation of totals. Add notes to each item. Calculate discounts.'
    },
    {
      id: 'h2-export-share',
      title: '📄 Export and Share',
      keywords: ['export', 'share', 'PDF', 'Excel', 'price quote', 'professional design', 'customize export', 'logo', 'contact details', 'payment terms'],
      searchableContent: "Export the project in professional formats. Export types: PDF (designed price quote), Excel (detailed file). Customize export: add logo, contact details, payment terms, document language."
    },
    {
      id: 'h2-subscription',
      title: '💳 Subscription Management',
      keywords: ['subscrition management', 'subscription plans', 'free', 'Premium', 'Enterprise', 'price', 'projects', 'export', 'AI', 'support', 'trial days', 'cancel subscription', 'upgrade'],
      searchableContent: "Several subscription plans: Free, Premium ($10/month), Enterprise ($30/month). All paid plans come with a 14-day trial. Subscription management: upgrade/downgrade, cancel, payment history, invoices, payment methods."
    },
    {
      id: 'h2-tips', // This ID refers to the FAQ section
      title: '❓ Frequently Asked Questions about CostingWizard',
      keywords: ['frequently asked questions', 'FAQ', 'create project', 'chapter', 'item', 'quantity calculation', 'prices', 'resources', 'AI', 'export', 'chapter order', 'subscription limit', 'data backup', 'resource multipliers', 'offline work'],
      searchableContent: "How to create a new project? What is the difference between a chapter and an item? How to calculate item quantity? What to do if there are no prices for resources? How to use AI? Can I export a price quote? Change order of chapters and items. Subscription limit. Data backup. Resource multipliers. Offline work."
    }
  ], []);

  // Smooth scroll function for navigation
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsNavOpen(false); // Close mobile nav after selection
      // Do not close FAQ accordion here, it's handled by its own state
    }
  };

  // Search functionality
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const lowerCaseQuery = query.toLowerCase();
    const results = sectionsData.filter(section =>
      section.title.toLowerCase().includes(lowerCaseQuery) ||
      (section.keywords && section.keywords.some(keyword =>
        keyword.toLowerCase().includes(lowerCaseQuery)
      )) ||
      (section.searchableContent && section.searchableContent.toLowerCase().includes(lowerCaseQuery))
    );
    setSearchResults(results);
  };

  useEffect(() => {
    // Handle anchor links on page load
    const hash = window.location.hash.substr(1);
    if (hash) {
      setTimeout(() => scrollToSection(hash), 100);
    }

    // Cleanup FAQ state on unmount
    return () => {
      setOpenFaqItem(null);
      setSearchResults([]);
    };
  }, []);

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif', direction: 'ltr' }}>
      <style>
        {`
          /* Documentation Styles */
          .doc-container {
            max-width: 1200px;
            margin: 0 auto;
            background: white;
            box-shadow: 0 0 20px rgba(0,0,0,0.1);
            min-height: 100vh;
          }
          .doc-header {
            background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
            color: white;
            padding: 60px 40px;
            text-align: center;
          }
          .doc-header h1 {
            margin: 0;
            font-size: 2.5em;
            font-weight: 300;
          }
          .doc-header p {
            margin: 10px 0 0 0;
            font-size: 1.2em;
            opacity: 0.9;
          }
          
          /* Search Styles */
          .search-container {
            background: #f8f9fa;
            padding: 20px;
            border-bottom: 1px solid #dee2e6;
          }
          .search-box {
            max-width: 500px;
            margin: 0 auto;
            position: relative;
          }
          .search-input {
            width: 100%;
            padding: 12px 45px 12px 20px;
            border: 2px solid #1976d2;
            border-radius: 25px;
            font-size: 16px;
            outline: none;
            direction: ltr;
            font-family: inherit;
          }
          .search-input:focus {
            box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
          }
          .search-icon {
            position: absolute;
            right: 15px;
            top: 50%;
            transform: translateY(-50%);
            color: #666;
          }
          .search-results {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            background: white;
            border: 1px solid #ddd;
            border-radius: 8px;
            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
            z-index: 1100;
            max-height: 300px;
            overflow-y: auto;
          }
          .search-result-item {
            padding: 12px 20px;
            cursor: pointer;
            border-bottom: 1px solid #f0f0f0;
            transition: background 0.2s;
          }
          .search-result-item:hover {
            background: #f8f9fa;
          }
          .search-result-item:last-child {
            border-bottom: none;
          }
          
          /* Navigation Styles */
          .doc-nav {
            background: #2c3e50;
            position: sticky;
            top: 0;
            z-index: 1000;
            box-shadow: 0 2px 5px rgba(0,0,0,0.1);
          }
          .nav-header {
            display: none;
            padding: 15px 20px;
            background: #34495e;
            color: white;
            justify-content: space-between;
            align-items: center;
          }
          .nav-toggle {
            background: none;
            border: none;
            color: white;
            font-size: 1.5em;
            cursor: pointer;
            padding: 5px;
          }
          .doc-nav ul {
            list-style: none;
            margin: 0;
            padding: 0;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
          }
          .doc-nav li {
            margin: 0;
          }
          .doc-nav a {
            display: block;
            padding: 15px 20px;
            color: white;
            text-decoration: none;
            transition: background 0.3s;
            cursor: pointer;
            white-space: nowrap;
          }
          .doc-nav a:hover {
            background: #34495e;
          }
          .doc-content {
            padding: 40px;
            line-height: 1.6;
            color: #333;
          }
          .section {
            margin-bottom: 60px;
          }
          .section h2 {
            color: #2c3e50;
            font-size: 2em;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 2px solid #1976d2;
            scroll-margin-top: 90px;
          }
          .section h3 {
            color: #34495e;
            font-size: 1.5em;
            margin: 30px 0 15px 0;
          }
          .section h4 {
            color: #34495e;
            font-size: 1.3em;
            margin: 24px 0 12px 0;
            padding-bottom: 10px;
            border-bottom: 1px solid #1976d2;
          }
          .section h5 {
            font-size: 1.3em;
            font-weight: bold;
            margin: 24px 0 12px 0;
            color: #34495e;
          }
          .section h6 {
            font-size: 1.1em;
            font-weight: bold;
            margin: 24px 0 12px 0;
            color: #34495e;
          }
          .section p {
            margin: 15px 0;
            line-height: 1.8;
          }
          .feature-list {
            background: #f8f9fa;
            border-radius: 8px;
            padding: 20px;
            margin: 20px 0;
          }
          .feature-list ul {
            margin: 10px 0;
            padding-left: 30px;
          }
          .feature-list li {
            margin: 8px 0;
          }
          .mockup {
            border: 2px solid #dee2e6;
            border-radius: 8px;
            margin: 30px 0;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
          }
          .mockup-title {
            background-color: #e9ecef;
            padding: 12px 20px;
            font-weight: bold;
            color: #495057;
            border-bottom: 1px solid #dee2e6;
            font-size: 0.9em;
          }
          .mockup-content {
            background: white;
            padding: 20px;
          }
          .highlight-box {
            background: #e8f4f8;
            border-left: 4px solid #1976d2;
            padding: 15px 20px;
            margin: 20px 0;
          }
          .warning-box {
            background: #fff3cd;
            border-left: 4px solid #ffc107;
            padding: 15px 20px;
            margin: 20px 0;
          }
          .success-box {
            background: #d4edda;
            border-left: 4px solid #28a745;
            padding: 15px 20px;
            margin: 20px 0;
          }
          .app-mockup {
            background: #f5f5f5;
            border-radius: 8px;
            padding: 20px;
            margin: 20px 0;
            font-family: Arial, sans-serif;
          }
          .dashboard-card {
            background: white;
            border-radius: 8px;
            padding: 20px;
            margin: 10px 0;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          .pricing-card {
            background: linear-gradient(45deg, #f5f5f5 30%, #ffffff 90%);
            border: 1px solid #ddd;
            border-radius: 8px;
            padding: 20px;
            margin: 10px;
            text-align: center;
          }
          .step-number {
            background: #1976d2;
            color: white;
            border-radius: 50%;
            width: 30px;
            height: 30px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            margin-right: 10px;
            font-weight: bold;
          }
          .back-button {
            position: fixed;
            top: 20px;
            left: 20px;
            background: #1976d2;
            color: white;
            border: none;
            padding: 10px 15px;
            border-radius: 5px;
            text-decoration: none;
            z-index: 1001;
            box-shadow: 0 2px 5px rgba(0,0,0,0.2);
          }
          .back-button:hover {
            background: #1565c0;
            color: white;
            text-decoration: none;
          }
          
          /* FAQ Accordion Styles */
          .faq-card {
            transition: all 0.3s ease;
            direction: ltr;
          }
          .faq-card:hover {
            transform: translateY(-1px) !important;
          }
          
          /* Animation for content reveal */
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          /* Apple and Google Badges */
          .app-store-badges {
            display: flex;
            gap: 12px;
            margin-top: 8px;
            flex-wrap: wrap;
            align-items: center;
          }
          
          .app-store-link {
            display: inline-block;
            transition: transform 0.2s ease;
          }
          
          .app-store-link:hover {
            transform: scale(1.05);
          }
          
          .app-store-badge {
            height: 50px;
            width: auto;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          
          /* Mobile Responsive */
          @media (max-width: 768px) {
            .doc-content {
              padding: 20px;
            }
            .doc-header {
              padding: 40px 20px;
            }
            .doc-header h1 {
              font-size: 1.8em;
            }
            .nav-header {
              display: flex;
            }
            .doc-nav ul {
              display: ${isNavOpen ? 'flex' : 'none'};
              flex-direction: column;
              position: absolute;
              top: 100%;
              left: 0;
              right: 0;
              background: #2c3e50;
              box-shadow: 0 4px 15px rgba(0,0,0,0.2);
            }
            .doc-nav a {
              padding: 18px 20px;
              border-bottom: 1px solid #34495e;
              font-size: 1.1em;
            }
            .doc-nav a:last-child {
              border-bottom: none;
            }
            .back-button {
              position: sticky;
              top: 0;
              left: 0;
              right: 0;
              width: 100vw;
              margin: 0;
              display: block;
              z-index: 2002;
              border-radius: 0 0 8px 8px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.08);
              text-align: center;
              padding: 14px 0;
              font-size: 1.1em;
            }
            .search-container {
              padding: 15px;
            }
            .search-input {
              padding: 15px 45px 15px 20px;
              font-size: 16px;
            }
            .section h2 {
              scroll-margin-top: 125px;
            }
            .mockup-content {
              padding: 15px;
            }
            .pricing-card {
              margin: 10px 0;
            }
            .step-number {
              margin-left: 10px;
              margin-right: 0;
            }
            
            /* Mobile Apple and Google Badges */
            .app-store-badges {
              flex-direction: column;
              gap: 16px;
              margin-top: 16px;
              align-items: flex-start;
            }
            
            .app-store-badge {
              height: 60px;
            }
          }
          
          /* Extra small screens */
          @media (max-width: 480px) {
            .doc-header {
              padding: 30px 15px;
            }
            .doc-header h1 {
              font-size: 1.5em;
            }
            .doc-content {
              padding: 15px;
            }
            .search-container {
              padding: 10px;
            }
            .mockup-content {
              padding: 10px;
            }
            
            /* Extra small screens - Apple and Google Badges */
            .app-store-badges {
              gap: 20px;
              margin-top: 20px;
              align-items: center;
            }
            
            .app-store-badge {
              height: 70px;
            }
          }
        `}
      </style>

      <div className="doc-container">
        <Link 
          to={isAuthenticated ? "/c/dashboard" : "/"} 
          className="back-button"
        >
          ← {isAuthenticated ? "Back to Dashboard" : "Back to Home"}
        </Link>

        <div className="doc-header">
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: '16px',
            marginBottom: '24px',
            flexDirection: 'row' // Changed from row-reverse for LTR
          }}>
            <img 
              src="/logo.png" 
              alt="Costing Wizard Logo" 
              style={{
                width: '90px',
                height: '90px',
                borderRadius: '50%',
                objectFit: 'cover',
                boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                background: '#fff'
              }} 
            />
            <div style={{ textAlign: 'left' }}>
              <h1 style={{ 
                margin: 0, 
                fontSize: '2.5em', 
                fontWeight: 300,
                background: 'linear-gradient(135deg, #ffffff 0%, #e3f2fd 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                direction: 'ltr',
                display: 'inline-block'
              }}>
                Costing Wizard
              </h1>
              <p style={{ 
                margin: '8px 0 0 0', 
                fontSize: '1.2em', 
                opacity: 0.9,
                color: '#e3f2fd'
              }}>
                Smart system for cost calculation and professional quotes
              </p>
            </div>
          </div>
          {isAuthenticated && user && (
            <p style={{ fontSize: '1em', marginTop: '20px' }}>
              👋 Hello {user.displayName || user.email}!
            </p>
          )}
        </div>
        
        {/* Search Section */}
        <div className="search-container">
          <div className="search-box">
            <input
              type="text"
              className="search-input"
              placeholder="Search in guide... (e.g. AI, export, subscription)"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
            <div className="search-icon">🔍</div>
            
            {searchResults.length > 0 && searchQuery && (
              <div className="search-results">
                {searchResults.map((result) => (
                  <div
                    key={result.id}
                    className="search-result-item"
                    onClick={() => {
                      scrollToSection(result.id);
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                  >
                    {result.title}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="doc-nav">
          <div className="nav-header">
            <span>Navigation Menu</span>
            <button 
              className="nav-toggle"
              onClick={() => setIsNavOpen(!isNavOpen)}
            >
              {isNavOpen ? '✕' : '☰'}
            </button>
          </div>
          <ul>
            <li><a onClick={() => scrollToSection('h2-overview')}>🏗️ Overview</a></li>
            <li><a onClick={() => scrollToSection('h2-videos')}>📺 Video Tutorials</a></li>
            <li><a onClick={() => scrollToSection('h2-getting-started-main')}>🚀 Getting Started</a></li>
            <li><a onClick={() => scrollToSection('h2-add-chapters-items')}>🧱 Adding Chapters and Items to a Project</a></li>
            <li><a onClick={() => scrollToSection('h2-add-location-quantities')}>📍 Adding Location and Quantities to an Item</a></li>
            <li><a onClick={() => scrollToSection('h2-dashboard')}>📊 Dashboard</a></li>
            <li><a onClick={() => scrollToSection('h2-ai-features')}>🤖 Artificial Intelligence</a></li>
            <li><a onClick={() => scrollToSection('h2-advanced-features')}>✨ Advanced Features</a></li>
            <li><a onClick={() => scrollToSection('h2-export-share')}>📄 Export and Share</a></li>
            <li><a onClick={() => scrollToSection('h2-subscription')}>💳 Subscription Management</a></li>
            <li><a onClick={() => scrollToSection('h2-tips')}>❓ Frequently Asked Questions</a></li>
          </ul>
        </nav>
        
        <div className="doc-content">
          {/* Overview Section */}
          <section id="h2-overview" className="section"> {/* Ensure section ID matches sectionsData ID */}
            <h2 id="h2-overview">🏗️ Overview</h2>
            <p>Costing Wizard is an advanced tool designed for professionals, contractors, and entrepreneurs to create accurate and professional price quotes. The system combines smart cost calculations, project management, and artificial intelligence for a perfect user experience. Key features: automatic cost calculation, custom templates, artificial intelligence, professional export, currency management, cloud backup, multi-device access. Perfect for: contractors, professionals, planners, consultants, and anyone needing a professional tool for cost calculation and price quotes. All users get free access to all features with basic limitations. You can upgrade to a Premium or Enterprise subscription at any time.</p>
            
            <div className="feature-list">
              <h3>🌟 Key Features</h3>
              <ul>
                <li><strong>Automatic Cost Calculation</strong> - Advanced calculator for all types of work</li>
                <li><strong>Custom Templates</strong> - Save templates for repetitive tasks</li>
                <li><strong>Artificial Intelligence</strong> - Create projects using AI</li>
                <li><strong>Professional Export</strong> - PDF and Excel formats</li>
                <li><strong>Cloud Backup</strong> - Automatic and secure cloud storage</li>
                <li><strong>Multi-device Access</strong> - Works on Android and iPhone, iPAD devices</li>
              </ul>
            </div>
            
            <div className="highlight-box">
              <strong>Perfect for:</strong> contractors, professionals, planners, consultants, and anyone needing a professional tool for cost calculation and price quotes.
            </div>
          </section>
          
          <div className="success-box" style={{marginBottom: '32px'}}>
            <strong>💡 Tip:</strong> All users get free access to all features with basic limitations. You can upgrade to a Premium or Enterprise subscription at any time.
          </div>

          {/* Video Tutorials Section */}
          <section id="h2-videos" className="section">
            <h2 id="h2-videos">📺 Video Tutorials</h2>
            <p>Learn how to use CostingWizard through detailed video tutorials. The videos demonstrate every feature in a clear and visual way.</p>
            
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',                    // Mobile: Stack vertically
                  sm: '1fr',                    // Small tablets: Still stack
                  md: '1fr 350px',             // Desktop: Video left, sidebar right
                  lg: '1fr 400px',             // Large desktop: Optimal size
                },
                gap: { xs: 2, sm: 3, md: 4 },  // Responsive gap
                mt: 3,
                mb: 3,
                px: { xs: 1, sm: 0 }           // Padding on mobile
              }}
            >
              {/* Embedded Video Player */}
              <Box sx={{ position: 'relative', order: { xs: 2, md: 1 } }}>
                <Box
                  sx={{
                    position: 'relative',
                    paddingBottom: '56.25%', // 16:9 aspect ratio
                    height: 0,
                    overflow: 'hidden',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    bgcolor: '#000'
                  }}
                >
                  <iframe
                    key={currentVideo?.videoId || 'NDb5ASZ2t2c'}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      borderRadius: '12px'
                    }}
                    src={`https://www.youtube.com/embed/${currentVideo?.videoId || 'NDb5ASZ2t2c'}?vq=hd720&quality=hd720&rel=0&modestbranding=1&fs=1&autoplay=0`}
                    title={currentVideo?.title || "Quick Start Guide - Costing Wizard"}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </Box>
                
                <Box sx={{ mt: 2, px: { xs: 1, sm: 0 } }}>
                  <Box
                    component="h3"
                    sx={{
                      m: 0,
                      mb: 1,
                      color: '#2c3e50',
                      fontSize: { xs: '1.1rem', sm: '1.25rem' },
                      fontWeight: 'bold',
                      lineHeight: 1.3
                    }}
                  >
                    {currentVideo?.title || "Quick Start Guide - Costing Wizard"}
                  </Box>
                  <Box
                    component="p"
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '0.95rem' },
                      color: '#666',
                      m: 0,
                      lineHeight: 1.4
                    }}
                  >
                    Duration: {currentVideo?.duration || "05:16"} • Click on another video from the list to watch
                  </Box>
                </Box>
              </Box>

              {/* Video Guide Section */}
              <Box sx={{ order: { xs: 1, md: 2 } }}>
                <VideoGuideSection
                  title="Featured Video Tutorials"
                  videos={videoGuideData}
                  onVideoClick={handleVideoClick}
                  onShowAllClick={handleShowAllVideos}
                  showAllCoursesText="View All Videos on Our Channel"
                  maxVideos={4}
                  currentVideoId={currentVideo?.videoId}
                />
              </Box>
            </Box>
            
            <div className="highlight-box" style={{ marginTop: '30px' }}>
              <h4 style={{ margin: '0 0 15px 0' }}>💡 Tips for Effective Learning</h4>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li><strong>Follow the Order</strong> - Watch videos in the suggested order</li>
                <li><strong>Practice in Real-Time</strong> - Open the app while watching</li>
                <li><strong>Pause and Try</strong> - Pause the video and try it yourself</li>
                <li><strong>Refer to the Guide</strong> - Use the written guide for additional details</li>
              </ul>
            </div>
            
            <div className="success-box" style={{ marginTop: '20px', textAlign: 'center' }}>
              <strong>🎯 New videos are added monthly!</strong><br/>
              Follow updates to learn about new features
            </div>
          </section>
          
          {/* Getting Started Section - encompassing first project, etc. */}
          <section id="h2-getting-started-main" className="section">
            <h2 id="h2-getting-started-main">🚀 Getting Started</h2>         
            <h3>First Steps</h3>
            <div className="mockup">
              <div className="mockup-title">Registration and Login Process</div>
              <div className="mockup-content">
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                  <span className="step-number">1</span>
                  <div style={{ flex: 1 }}>
                    <span>Download the app from the </span>
                    <div className="app-store-badges">
                      <a 
                        href="https://apps.apple.com/app/apple-store/id6742328319?pt=127652282&ct=website_user_guide&mt=8" 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="app-store-link"
                      >
                        <img 
                          src="/images/app-store-badge.png" 
                          alt="Download on Apple" 
                          className="app-store-badge"
                        />
                      </a>
                      <a 
                        href="https://play.google.com/store/apps/details?id=com.tovsoftware.costingapp&referrer=utm_source%3Dwebsite%26utm_medium%3Duser_guide%26utm_campaign%3Dcosting_wizard%26utm_content%3Dguide_cta" 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="app-store-link"
                      >
                        <img 
                          src="/images/google-play-badge.png" 
                          alt="Get it on Google Play" 
                          className="app-store-badge"
                        />
                      </a>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                  <span className="step-number">2</span>
                  <span>Go to costingwizard.com or click Register in the app</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                  <span className="step-number">3</span>
                  <span>Click on Register and create a new account</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                  <span className="step-number">4</span>
                  <span>Verify your email address</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                  <span className="step-number">5</span>
                  <span>Log in through the app</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span className="step-number">6</span>
                  <span>Create your first project!</span>
                </div>
                <div className="success-box" style={{margin: '24px 0'}}>
                  <strong>💡 Tip:</strong> All users get free access to all features with basic limitations. You can upgrade to a Premium or Enterprise subscription at any time.
                </div>
              </div>
            </div>
            
            {/* This h2-first-project is part of h2-getting-started-main for search purposes but can have its own nav link */}
            <h2 id="h2-first-project">Creating Your First Project</h2> 
            <div className="feature-list">
              <h4>📋 Project List Screen</h4>
              <p>Welcome to the CostingWizard app!</p>
              <div style={{textAlign: 'center', margin: '16px 0 24px 0', display: 'flex', justifyContent: 'center', gap: '32px', flexWrap: 'wrap'}}>
                <div>
                  <a href="/images/guide/create_new_project.jpeg" target="_blank" rel="noopener noreferrer">
                    <img src="/images/guide/create_new_project.jpeg" alt="Project List Screen" style={{maxWidth: '240px', width: '70%', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', cursor: 'pointer'}} />
                  </a>
                  <div style={{fontSize: '1em', color: '#666', marginTop: '8px'}}>Project List Screen</div>
                </div>
                <div>
                  <a href="/images/guide/project_details.jpeg" target="_blank" rel="noopener noreferrer">
                    <img src="/images/guide/project_details.jpeg" alt="Project Details" style={{maxWidth: '240px', width: '70%', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', cursor: 'pointer'}} />
                  </a>
                  <div style={{fontSize: '1em', color: '#666', marginTop: '8px'}}>New Project Details</div>
                </div>
              </div>
              
              <p>On this screen, you can see all the projects you have already created. Each row in the table represents a project and includes:</p>
              <ul>
                <li><strong>Date</strong> – When the project was created.</li>
                <li><strong>ID</strong> – Unique code for identifying the project.</li>
                <li><strong>Description</strong> – Title or summary of the project.</li>
              </ul>
              
              <h4>➕ Creating a New Project</h4>
              <p>At the bottom of the screen, you will find two buttons:</p>
              
              <div className="highlight-box">
                <h5>1. Add New Project</h5>
                <p>To create a project manually:</p>
                <ul>
                  <li>Click on "Add New Project".</li>
                  <li>Fill in the project name, description, ID, and any other required details in the next steps.</li>
                </ul>
              </div>
              
              <div className="highlight-box">
                <h5>2. Add Project with AI</h5>
                <p>To create a project template automatically using AI:</p>
                <ul>
                  <li>Click on "Add Project with AI".</li>
                  <li>You will be asked to type a general description of the project (e.g., "Kitchen renovation of 15 square meters. The kitchen includes replacing wardrobes, kitchen counter, new tiles, and painting the walls.").</li>
                  <li>The app will build a complete project for you, including items, quantities, and materials.</li>
                  <li>After the project is created, you can edit it and add more details by editing the project with AI.</li>
                </ul>
              </div>
            </div>
           </section>

          <section id="h2-add-chapters-items" className="section">
            <h2 id="h2-add-chapters-items">🧱 Adding Chapters and Items to a Project</h2>
            <div className="feature-list">
              <p>After creating a new project, you will build its budget by adding chapters (main areas/stages) and for each chapter – items (detailed tasks). What is a chapter? A chapter is a group of works from the same field. What is an item? An item is a specific task or component within a chapter. For each item, you define: description, quantity, unit of measure, price per unit, notes. How to add chapters and items in practice? Adding a chapter, adding an item to a chapter. Defining a price for an item: fixed price or resource-based pricing.</p>
              <h5>🧩  What is a chapter?</h5>
              <p>A chapter is a group of works from the same field, for example:</p>
              <div style={{textAlign: 'center', margin: '16px 0 20px 0'}}>
                <a href="/images/guide/save_new_chapter.jpeg" target="_blank" rel="noopener noreferrer">
                  <img src="/images/guide/save_new_chapter.jpeg" alt="Adding a New Chapter" style={{maxWidth: '240px', width: '70%', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', cursor: 'pointer'}} />
                </a>
                <div style={{fontSize: '1em', color: '#666', marginTop: '8px'}}>Adding a New Chapter</div>
              </div>
              <ul>
                <li>Demolition work</li>
                <li>Construction work</li>
                <li>Electrical work and plastering</li>
                <li>Plumbing</li>
              </ul>
              <p>You can add multiple chapters to a project – each one representing a different stage of work.</p>
              <h5>🧾 What is an item?</h5>
              <p>An item is a specific task or component within a chapter, for example in the "Demolition work" chapter: removing flooring in a bathroom, removing tiles, and painting the walls.</p>
              <ul>
                <li>Removing flooring in a bathroom – 12 square meters</li>
                <li>Removing tiles – 2 square meters</li>
                <li>Removing old plaster – 30 square meters</li>
              </ul>
              <p>For each item, you define:</p>
              <ul>
                <li><strong>Description</strong> (what we're doing)</li>
                <li><strong>Quantity</strong> (e.g., 12 square meters)</li>
                <li><strong>Unit of Measure</strong> (e.g., square meters, cubic meters, hours)</li>
                <li><strong>Price per Unit</strong></li>
                <li><strong>Notes</strong> (optional)</li>
              </ul>
              <h5>➕ How do we add chapters and items in practice?</h5>
              <div>
                In the CostingWizard app, you can add chapters and items in a simple and intuitive way – like this:
                <br/><br/>
                <b>🔹 Adding a Chapter (Main Area/Stage)</b><br/>
                You can:
                <ul>
                  <li>Add a new chapter from scratch (e.g., "Demolition work").</li>
                  <li>Or select an existing chapter and add items to it.</li>
                </ul>
                To do this:
                <ul>
                  <li>Click on "Add Chapter" in the project screen.</li>
                  <li>Enter the chapter name (e.g., "Demolition work").</li>
                </ul>
                <b>🔸 Adding an Item to a Chapter</b><br/>
                Within each chapter, you can add multiple items. For example, in the "Demolition work" chapter, you can add items like "Removing flooring in a bathroom", "Removing tiles", and "Painting the walls".<br/>
                To add an item:
                <ul>
                  <li>Click on "Add Item" in the chapter screen.</li>
                  <li>Enter the item name, description, quantity, unit of measure, and price per unit.</li>
                </ul>
                <b>💰 Defining a Price for an Item</b><br/>
                You have two options:
                <ul>
                  <li><b>✅ Fixed Price:</b><br/>
                    If the item doesn't require any resources, just enter the price per unit in the app.<br/>
                    For example:<br/>
                    Quantity: 10 square meters<br/>
                    Price per unit: 50 $<br/>
                    Total: 500 $<br/>
                   <div style={{textAlign: 'center', margin: '16px 0 20px 0'}}>
                     <a href="/images/guide/item_without_resrouces.jpeg" target="_blank" rel="noopener noreferrer">
                       <img src="/images/guide/item_without_resrouces.jpeg" alt="Item without Resources" style={{maxWidth: '240px', width: '70%', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', cursor: 'pointer'}} />
                     </a>
                     <div style={{fontSize: '1em', color: '#666', marginTop: '8px'}}>This image shows an item without resources</div>
                 </div>
                  </li>
                  <li><b>🧱 Resource-Based Pricing:</b><br/>
                    If the item requires resources, enter the quantity and price per unit for each resource. The app will calculate the total cost for you.<br/>
                    For example, in the "Removing flooring in a bathroom" item, you would enter:<br/>
                    Quantity: 10 square meters<br/>
                    Price per unit: 50 $<br/>
                    Total: 500 $<br/>
                    <br/>
                    <b>💡 No need to enter a price per unit – it will be calculated automatically.</b>
                  </li>
                </ul>
              </div>
              <div className="highlight-box">
                💡 <b>Tip:</b> The total cost for each item and chapter is calculated automatically based on the quantity and price per unit. This way, you always know the total cost of each stage of the project.
              </div>
            </div>
           </section>

          <section id="h2-add-location-quantities" className="section">
            <h2 id="h2-add-location-quantities">📍 Adding Location and Quantities to an Item</h2>
            <div className="feature-list">
                <p>
                  At this stage, you can enter the item's location in the project and the actual quantity, determined by four multipliers. How do the multipliers work? Quantity = Multiplier 1 × Multiplier 2 × Multiplier 3 × Multiplier 4. Example: Removing flooring in a bathroom, multipliers for the width and length of the room.
                </p>
                <h6>🔢 How do the multipliers work?</h6>
                <p>
                  The app calculates the total quantity based on the multipliers you enter. For example, if you enter 2 for the width multiplier, 3 for the length multiplier, 1 for the height multiplier, and 1 for the number of rooms, the app will calculate the total quantity as 2 × 3 × 1 × 1 = 6 square meters.
                </p>
                <p>
                  This is a flexible and easy way to calculate quantities based on different dimensions or components. If you don't need all four multipliers, just leave them at 1.0.
                </p>
                <h6>📌 Example:</h6>
                <ul>
                  <li><b>Item:</b> Removing flooring in a bathroom</li>
                  <li><b>Location:</b> Bathroom (1 floor, 1 room)</li>
                  <li><b>Unit of Measure:</b> Square meters</li>
                  <li><b>Quantity:</b> 6 square meters</li>
                </ul>
                <b>Multipliers:</b>
                <ul>
                  <li>Multiplier 1 (Width of the room): 2.0 meters</li>
                  <li>Multiplier 2 (Length of the room): 3.0 meters</li>
                  <li>Multiplier 3, 4: Not needed – leave them at 1.0</li>
                </ul>
                <p><b>⚙️ Calculated Quantity:</b> 2 × 3 × 1 × 1 = 6 square meters</p>
                <p><b>🧾 Total Cost:</b> 6 square meters × 120 $ = 720 $</p>
              </div>
          </section>
          
          {/* Dashboard Section */}
          <section id="h2-dashboard" className="section">
            <h2 id="h2-dashboard">📊 Dashboard</h2>
            <p>The dashboard is your command center. Here you can find all your projects, usage statistics, and subscription details. You can also manage your subscription and access additional features.</p>
            
            <div className="mockup">
              <div className="mockup-title">Main Dashboard</div>
              <div className="mockup-content">
                <div className="app-mockup">
                  <div className="dashboard-card">
                    <h3>📋 Subscription Details</h3>
                    <p><strong>Hello, user@example.com</strong></p>
                    <p>Subscription Type: Premium | Currency: $</p>
                  </div>
                  
                  <div className="dashboard-card">
                    <h3>📊 Monthly Usage</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', textAlign: 'center' }}>
                      <div>
                        <div style={{ fontSize: '2em' }}>📄</div>
                        <div>PDF Export: 15</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '2em' }}>📊</div>
                        <div>Excel Export: 8</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '2em' }}>🤖</div>
                        <div>AI Calls: 25</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '2em' }}>📁</div>
                        <div>Projects: 12</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <h3>Components of the Dashboard</h3>
            
            <h4>Subscription Details</h4>
            <ul>
              <li>Personal Information and Account Details</li>
              <li>Current Subscription Status</li>
              <li>Currency Settings</li>
              <li>Links to Subscription Management</li>
            </ul>
            
            <h4>Usage Data</h4>
            <ul>
              <li>PDF and Excel export counter</li>
              <li>Artificial intelligence usage</li>
              <li>Number of saved projects</li>
              <li>Progress compared to subscription limits</li>
            </ul>
          </section>

           {/* AI Features Section */}
           <section id="ai-features" className="section">
            <h2 id="h2-ai-features">🤖 Artificial Intelligence</h2>
            <p>Our new feature allows you to create projects using artificial intelligence. Simply describe what you want to build, and the system will create a list of items for you. How to use AI: Describe in detail, include dimensions, specify type of work, check the result, edit as needed. The AI provides an initial estimate only.</p>
            
            <div className="mockup">
              <div className="mockup-title">Creating a Project with AI</div>
              <div className="mockup-content">
                <div className="app-mockup">
                  <h4>Describe Your Project:</h4>
                  <div style={{ background: '#f0f8ff', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                    <textarea 
                      style={{ 
                        width: '100%', 
                        height: '80px', 
                        border: '1px solid #ddd', 
                        borderRadius: '5px', 
                        padding: '10px',
                        resize: 'none'
                      }}
                      value="I want to renovate a 15 square meter kitchen. The kitchen includes replacing wardrobes, kitchen counter, new tiles, and painting the walls."
                      readOnly
                    />
                  </div>
                  
                  <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
                    <h4>AI Generated for You:</h4>
                    <ul style={{ marginTop: '10px' }}>
                      <li>6 square meters of wardrobe replacement – $3,000 for 6 square meters</li>
                      <li>4 square meters of kitchen counter – $2,500 for 4 square meters</li>
                      <li>4 square meters of kitchen tiles – $800 for 4 square meters</li>
                      <li>1 unit of kitchen renovation – $1,200 for 1 unit</li>
                      <li>1 unit of kitchen painting – $350 for 1 unit</li>
                      <li>25 square meters of wall painting – $50 for 25 square meters</li>
                    </ul>
                    <p style={{ marginTop: '15px', fontWeight: 'bold' }}>Total Estimated: $32,000</p>
                  </div>
                </div>
              </div>
            </div>
            
            <h3>How to Use AI</h3>
            <div className="feature-list">
              <ol>
                <li><strong>Describe in Detail</strong> - Tell us what you want to build or renovate</li>
                <li><strong>Include Dimensions</strong> - Mention any specific dimensions or sizes you have</li>
                <li><strong>Specify Type of Work</strong> - Whether it's construction, renovation, electrical, plumbing, etc.</li>
                <li><strong>Check the Result</strong> - The AI might make a mistake, but it's always worth checking</li>
                <li><strong>Edit as Needed</strong> - Adjust prices and quantities based on real-world data</li>
              </ol>
            </div>
            
            <div className="warning-box">
              <strong>⚠️ Important Note:</strong> The AI provides an initial estimate only. Always check prices with suppliers and professionals before finalizing a quote.
            </div>
          </section>
          
          {/* Projects Section */}
          <section id="projects" className="section"> 
            <h2 id="h2-advanced-features">Advanced Features</h2>
            <div className="feature-list">
              <ul>
                <li><strong>Duplicate Projects</strong> - Copy an existing project as a base for a new one</li>
                <li><strong>Custom Templates</strong> - Save templates for repetitive tasks</li>
                <li><strong>Automatic Calculation</strong> - Update totals automatically with changes in quantities</li>
                <li><strong>Additional Notes and Information</strong> - Add notes to each item</li>
                <li><strong>Discounts and Reductions</strong> - Calculate discounts at the item or project level</li>
              </ul>
            </div>
          </section>
          
         
          
          {/* Export Section */}
          <section id="export" className="section">
            <h2 id="h2-export-share">📄 Export and Share</h2>
            <p>After creating the project, you can export it in various professional formats and share it with clients and collaborators.</p>      
            <h3>Types of Export Available</h3>
            <div className="mockup">
              <div className="mockup-title">Export Options</div>
              <div className="mockup-content">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                  <div style={{ background: '#fff5f5', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '3em', marginBottom: '10px' }}>📄</div>
                    <h4>PDF Export</h4>
                    <p style={styles.paragraph}>
  Creating a designed price quote and structured document ready for printing or sending to clients.
</p>
                    <ul style={{ textAlign: 'left', marginTop: '15px' }}>
                      <li>Professional design and layout</li>
                      <li>Complete breakdown of all chapters and items</li>
                      <li>Includes quantities, prices, and notes</li>
                    </ul>
                  </div>
                  
                  <div style={{ background: '#f0fff4', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '3em', marginBottom: '10px' }}>📊</div>
                    <h4>Excel Export</h4>
                    <p style={styles.paragraph}>Exporting the entire project to Excel for detailed analysis and editing. It includes all chapters, items, and resources, providing a complete and clear view of the budget.</p>
                    <ul style={{ textAlign: 'left', marginTop: '15px' }}>
                      <li>Complete breakdown of all stages of the project</li>
                      <li>Includes quantities, costs, resources, and notes</li>
                      <li>Easy to edit, filter, and summarize</li>
                      <li>Great for sharing with clients or internal use</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <h3>Customization</h3>
            <div className="feature-list">
              <ul>
                <li><strong>Adding a Logo</strong> - Add your company logo for professional branding</li>
                <li><strong>Contact Details</strong> - Include your personal and company contact information</li>
                <li><strong>Payment Terms</strong> - Set standard payment terms</li>
                <li><strong>Additional Notes and Notes</strong> - Add important notes and notes</li>
                <li><strong>Document Language</strong> - Choose between Hebrew and English</li>
              </ul>
            </div>
          </section>
          
          {/* Subscription Section */}
          <section id="subscription" className="section">
            {/* <h2 id="h2-subscription">💳 Subscription Management</h2>
            <p>The system offers several subscription plans to suit different types of users – from individual freelancers to large teams.</p> */}
{/*             
            <div className="mockup">
              <div className="mockup-title">Available Subscription Plans</div>
              <div className="mockup-content">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                  <div className="pricing-card">
                    <h3>🆓 Free</h3>
                    <div style={{ fontSize: '2em', margin: '15px 0' }}>$0</div>
                    <ul style={{ textAlign: 'right' }}>
                      <li>Up to 2 projects</li>
                      <li>Up to 3 PDF/Excel exports</li>
                      <li>2 AI calls</li>
                      <li>Basic support</li>
                    </ul>
                  </div>
                  <div className="pricing-card" style={{ border: '2px solid #1976d2' }}>
                    <h3>🧮 Premium</h3>
                    <div style={{ fontSize: '2em', margin: '15px 0' }}>$30<small>/month</small></div>
                    <ul style={{ textAlign: 'right' }}>
                      <li>Up to 8 projects</li>
                      <li>Up to 20 PDF/Excel exports</li>
                      <li>10 AI calls</li>
                      <li>Preferred support</li>
                    </ul>
                  </div>
                  <div className="pricing-card">
                    <h3>🚀 Enterprise Plus</h3>
                    <div style={{ fontSize: '2em', margin: '15px 0' }}>$109<small>/month</small></div>
                    <ul style={{ textAlign: 'right' }}>
                      <li>All features</li>
                      <li>AI limited to 50</li>
                      <li>Customized branding</li>
                      <li>VIP support</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div> */}
            
            {/* <div className="highlight-box">
              <strong>💡 Tip:</strong> All plans come with a 14-day free trial. You can cancel at any time without any commitment.
            </div>
            
            <h3>Your Subscription</h3>
            <ul>
              <li>Upgrading/downgrading at any time</li>
              <li>Canceling through the client portal</li>
              <li>Viewing payment history</li>
              <li>Downloading invoices</li>
              <li>Changing payment methods</li>
            </ul> */}
          </section>
          
          {/* Tips Section */}
          <section id="tips" className="section">
            <h2 id="h2-tips">❓ Frequently Asked Questions</h2>
            <p style={{ textAlign: 'center', marginBottom: '30px', color: '#666', fontSize: '1.1em' }}>
              Find quick answers to the most common questions about using the system
            </p>
            
            <div style={{ display: 'grid', gap: '20px', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))' }}>
              {[ // Array of Q&A objects
                {
                  q: "How do we create a new project?",
                  a: "On the main screen, click on \"Add New Project\" for a manual project, or \"Add Project with AI\" to create a quote automatically based on a description you provide. The system will build a complete project for you.",
                  icon: "🏗️"
                },
                {
                  q: "What's the difference between a chapter and an item?",
                  a: "A chapter is a broad field of work (e.g., electrical, renovation, plumbing). An item is a specific task or component within a chapter (e.g., replacing wardrobes, painting the walls).",
                  icon: "📋"
                },
                {
                  q: "How do we calculate the quantity of an item?",
                  a: "The quantity is calculated automatically based on four multipliers: for example, width × length × height × number of rooms. If you don't need all four multipliers, just leave them at 1.",
                  icon: "🧮"
                },
                {
                  q: "What do we do if we don't have prices for resources?",
                  a: "You can add an item without resources and enter a price per unit manually – this is a quick and easy solution when you have a total price inclusive of all resources.",
                  icon: "💰"
                },
                {
                  q: "How do we use AI in the app?",
                  a: "Provide as much detail about the project as possible: type of work, size, materials, requirements. The system will suggest a basic structure of chapters and items for you to edit, delete, or add to.",
                  icon: "🤖"
                },
                {
                  q: "Can we export a price quote?",
                  a: "Yes. You can export the project to PDF or Excel through the quote screen, and easily print or send it to clients.",
                  icon: "📄"
                },
                {
                  q: "Can we change the order of chapters and items?",
                  a: "Absolutely. Drag and drop chapters and items to your preferred order – just like in project management.",
                  icon: "🔄"
                },
                {
                  q: "What happens when we reach the subscription limit?",
                  a: "You'll see a notification. You can upgrade your subscription to continue adding projects, exporting files, or using AI.",
                  icon: "⚠️"
                },
                {
                  q: "Do our data get backed up?",
                  a: "Yes. Your data is securely stored in Google's Firebase cloud, and is accessible from any device you log in with.",
                  icon: "☁️"
                },
                {
                  q: "How do resource multipliers work?",
                  a: "If an item requires resources, you can set up to four multipliers for each resource. For example: quantity of material × number of layers × number of walls × number of days. The total quantity for each resource will be calculated automatically based on the multipliers, and the pricing will be determined accordingly.",
                  icon: "⚙️"
                },
                {
                  q: "Can we work offline (without internet)?",
                  a: "Yes. You can add and edit data even when you're not connected to the internet. Once you're back online, your changes will sync to the cloud. However, you won't be able to export to Excel or create a project with AI in offline mode.",
                  icon: "📱"
                }
              ].map((item, index) => (
                <div key={index} 
                     className={`faq-card ${openFaqItem === index ? 'expanded' : ''}`}
                     onClick={() => setOpenFaqItem(openFaqItem === index ? null : index)}
                     style={{
                       background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                       border: '1px solid #e9ecef',
                       borderRadius: '12px',
                       padding: '20px',
                       cursor: 'pointer',
                       transition: 'all 0.3s ease',
                       boxShadow: openFaqItem === index 
                         ? '0 8px 25px rgba(25, 118, 210, 0.15)' 
                         : '0 2px 8px rgba(0,0,0,0.08)',
                       transform: openFaqItem === index ? 'translateY(-2px)' : 'translateY(0)',
                       borderColor: openFaqItem === index ? '#1976d2' : '#e9ecef'
                     }}>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    marginBottom: openFaqItem === index ? '15px' : '0'
                  }}>
                    <div style={{
                      fontSize: '1.5em',
                      flexShrink: 0,
                      marginTop: '2px'
                    }}>
                      {item.icon}
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <h4 style={{
                        margin: 0,
                        fontSize: '1.1em',
                        fontWeight: '600',
                        color: '#2c3e50',
                        lineHeight: '1.4'
                      }}>
                        {item.q}
                      </h4>
                      
                      {openFaqItem === index && (
                        <div style={{
                          marginTop: '12px',
                          color: '#495057',
                          lineHeight: '1.6',
                          fontSize: '0.95em',
                          animation: 'fadeIn 0.3s ease-in-out'
                        }}>
                          {item.a}
                        </div>
                      )}
                    </div>
                    
                    <div style={{
                      fontSize: '1.2em',
                      color: '#1976d2',
                      transition: 'transform 0.3s ease',
                      transform: openFaqItem === index ? 'rotate(45deg)' : 'rotate(0deg)',
                      flexShrink: 0
                    }}>
                      +
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="highlight-box" style={{ marginTop: '40px', textAlign: 'center' }}>
              <strong>💬 Didn't find what you were looking for?</strong><br/>
              Contact us and we'll be happy to help! Send an email to: support@costingwizard.com
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default UserGuide; 